import { useCallback, useState } from 'react';
import { ApiError, apiRequest } from './api';

interface MutationState {
  busy: boolean;
  error: ApiError | null;
}

/**
 * A write (POST/PATCH/PUT/DELETE) with busy and error state. Keeps every form
 * consistent: one place turns a thrown ApiError into a message the form can
 * show, and a network failure into a sentence rather than a stack trace.
 */
export function useMutation<T = unknown>() {
  const [state, setState] = useState<MutationState>({ busy: false, error: null });

  const run = useCallback(async (
    path: string,
    opts: { method?: string; body?: unknown } = {},
  ): Promise<T> => {
    setState({ busy: true, error: null });
    try {
      const res = await apiRequest<T>(path, { method: opts.method ?? 'POST', body: opts.body });
      setState({ busy: false, error: null });
      return res;
    } catch (err) {
      const e = err instanceof ApiError
        ? err
        : new ApiError(0, 'network', 'Could not reach the server. Check your connection and try again.');
      setState({ busy: false, error: e });
      throw e;
    }
  }, []);

  const clearError = useCallback(() => setState((s) => ({ ...s, error: null })), []);
  return { ...state, run, clearError };
}

/** Drops undefined / empty-string keys so optional fields are simply absent. */
export function compact<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== '' && v !== null) out[k] = v;
  }
  return out as Partial<T>;
}

/** Parses a form's numeric field to a number, or undefined when blank. */
export function numOrUndef(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}
